import { Link } from "wouter-preact";
import {
  AppRoutes, AuthGate, DebugPanel, Empty, Frame, Icon, isActive, registerFrame, registerRoutes, ShellPlaceholder, startApp,
  useI18n, useMenu, useRoute, useSession, type FrameProps, type MenuItem,
} from "@gimasi/giothub-shell";
import "./styles.css";

// The skeleton: config.json -> sign-in -> the frame config.json names
// (default: the shell's sidebar) around the pages (PLAN 2.1-2.4). The demo
// pages sit behind different permissions, so the menu and the route guard
// can be seen working.
const Demo = ({ title }: { title: string }) => () => <Empty icon="box" title={title} text="skeleton demo page" />;

registerRoutes([
  { path: "/", component: () => <><DebugPanel /><ShellPlaceholder app="skeleton" /></>, menu: { label: "debug_title", icon: "home" } },
  { path: "/alarms", component: Demo({ title: "Alarms" }), perm: "alarms:read",
    menu: { label: { en: "Alarms", de: "Alarme", it: "Allarmi", fr: "Alarmes" }, icon: "bell", group: "Demo" } },
  { path: "/rules", component: Demo({ title: "Rules" }), perm: "rules:manage",
    menu: { label: { en: "Rules", de: "Regeln", it: "Regole", fr: "Règles" }, icon: "bolt", group: "Demo" } },
  { path: "/assistant", component: Demo({ title: "Assistant" }), ai: true,
    menu: { label: { en: "Assistant", de: "Assistent", it: "Assistente", fr: "Assistant" }, icon: "star", group: "Demo" } },
]);

// A custom frame (FRAMES.md section 2): a plain top menu, built only from
// the frame hooks - config.json "frame": "skeleton-plain" selects it.
function PlainItem({ i, at }: { i: MenuItem; at: string }) {
  const on = isActive(i.href, at);
  return (
    <Link href={i.href} aria-current={on ? "page" : undefined}
      class={`flex items-center gap-2 rounded-control px-3 h-8 text-sm no-underline ${on ? "bg-surface shadow-surface text-ink font-medium" : "text-ink-2"}`}>
      {i.icon && <Icon n={i.icon} />}{i.label}
    </Link>
  );
}

function PlainFrame({ children }: FrameProps) {
  const { path } = useRoute();
  const { tenant, user, signOut } = useSession();
  const { t } = useI18n();
  return (
    <div class="min-h-dvh bg-canvas" data-frame="skeleton-plain">
      <header class="max-w-4xl mx-auto pt-4 px-4 flex items-center gap-3 text-sm">
        <span class="font-semibold grow">{tenant.name}</span><span class="text-muted">{user.name}</span>
        <button class="btn btn-sm" onClick={signOut}>{t("sign_out")}</button>
      </header>
      <nav aria-label={t("menu_main")} data-testid="menu" class="max-w-4xl mx-auto mt-3 px-4 flex flex-wrap items-center gap-1">
        {useMenu().map((e) => e.kind === "item"
          ? <PlainItem key={e.href} i={e} at={path} />
          : <span key={e.label} class="flex items-center gap-1" role="group" aria-label={e.label}>
              <span class="text-xs text-muted px-2">{e.label}</span>
              {e.items.map((i) => <PlainItem key={i.href} i={i} at={path} />)}
            </span>)}
      </nav>
      <main class="max-w-4xl mx-auto px-4">{children}</main>
    </div>
  );
}
registerFrame("skeleton-plain", PlainFrame);

startApp(() => (
  <AuthGate>
    <Frame><AppRoutes /></Frame>
  </AuthGate>
));
